import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { NavBar } from "./components/NavBar";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { VerifyPage } from "./pages/VerifyPage";
import { BrowsePage } from "./pages/BrowsePage";
import { ListingDetailPage } from "./pages/ListingDetailPage";
import { CreateListingPage } from "./pages/CreateListingPage";
import { MyListingsPage } from "./pages/MyListingsPage";
import { MessagesPage } from "./pages/MessagesPage";
import { AdminPage } from "./pages/AdminPage";
import { AcceptInvitePage } from "./pages/AcceptInvitePage";
import { AlertsPage } from "./pages/AlertsPage";
import { EditListingPage } from "./pages/EditListingPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-100 border-t-brand-600" />
    </div>
  );
}

// Wraps authenticated pages: gate on login, render the nav shell.
function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/gear/login" replace state={{ from: location.pathname }} />;
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavBar />
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 py-5 pb-24 sm:pb-8 safe-bottom">
        {children}
      </main>
    </div>
  );
}

// Admin-only pages. This is a UI guard, not the security boundary — the admin
// endpoints re-check server-side, so bypassing this gets you an empty page and
// a 403, not access.
function AdminOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/gear/login" replace />;
  if (!user.isAdmin) return <Navigate to="/gear" replace />;
  return children;
}

// Redirect already-signed-in users away from auth pages.
function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <FullScreenSpinner />;
  if (user) return <Navigate to="/gear" replace />;
  return children;
}

// Mounted by the shell under /gear/*, so these paths are RELATIVE — no leading
// slash. Anything that navigates with an absolute path (see the Navigate calls
// above and the links inside pages/) must include the /gear prefix itself.
export default function GearApp() {
  return (
    <Routes>
      <Route path="login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
      <Route path="verify" element={<PublicOnly><VerifyPage /></PublicOnly>} />
      <Route path="accept-invite" element={<PublicOnly><AcceptInvitePage /></PublicOnly>} />
      <Route path="forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="reset-password" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
      <Route path="alerts/unsubscribe" element={<UnsubscribePage />} />

      <Route index element={<Protected><BrowsePage /></Protected>} />
      <Route path="listing/:id" element={<Protected><ListingDetailPage /></Protected>} />
      <Route path="listing/:id/edit" element={<Protected><EditListingPage /></Protected>} />
      <Route path="sell" element={<Protected><CreateListingPage /></Protected>} />
      <Route path="my-listings" element={<Protected><MyListingsPage /></Protected>} />
      <Route path="messages" element={<Protected><MessagesPage /></Protected>} />
      <Route path="messages/:id" element={<Protected><MessagesPage /></Protected>} />
      <Route path="alerts" element={<Protected><AlertsPage /></Protected>} />
      <Route path="admin" element={<Protected><AdminOnly><AdminPage /></AdminOnly></Protected>} />

      <Route path="*" element={<Navigate to="/gear" replace />} />
    </Routes>
  );
}
